import type { OrgChartFieldKey, OrgChartFieldMapping, OrgChartRecord } from './types'

export const ORG_CHART_MAX_CANVAS_ROWS = 3

export const ORG_CHART_COLOR_PALETTE = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#eab308',
  '#84cc16',
  '#22c55e',
  '#10b981',
  '#14b8a6',
  '#06b6d4',
  '#0ea5e9',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#a855f7',
  '#d946ef',
  '#ec4899',
  '#f43f5e',
] as const

export interface OrgChartHierarchyNode {
  record: OrgChartRecord
  children: OrgChartHierarchyNode[]
}

export interface OrgChartHierarchy {
  roots: OrgChartHierarchyNode[]
  nodesById: Map<string, OrgChartHierarchyNode>
  parentById: Map<string, string | null>
}

export interface OrgChartDisplay {
  title: string
  subtitle?: string
  badges: string[]
  avatarUrl?: string
  initials: string
}

export interface OrgChartLegendEntry {
  value: string
  label: string
  color: string
}

export interface OrgChartCanvasEdge {
  sourceId: string
  targetId: string
}

function scalarToText(value: unknown): string {
  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value === 'string') {
    return value.trim()
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  return ''
}

function valuesForField(record: OrgChartRecord, field: OrgChartFieldKey | undefined): unknown[] {
  if (!field) {
    return []
  }

  return (Array.isArray(field) ? field : [field]).map((key) => record[key])
}

function arrayValueToText(value: unknown, useLastEntry: boolean): string {
  if (!Array.isArray(value)) {
    return scalarToText(value)
  }

  const values = value.map(scalarToText).filter(Boolean)
  if (useLastEntry) {
    return values.length > 0 ? values[values.length - 1] : ''
  }

  return values.join(', ')
}

export function resolveOrgChartField(
  record: OrgChartRecord,
  field: OrgChartFieldKey | undefined,
  options: { useLastArrayEntry?: boolean } = {}
): string {
  return valuesForField(record, field)
    .map((value) => arrayValueToText(value, options.useLastArrayEntry ?? false))
    .filter(Boolean)
    .join(' ')
}

function isAvatarUrl(value: string): boolean {
  return /^(?:https?:\/\/|data:image\/|blob:|\/\/)/i.test(value)
}

function avatarUrlForField(record: OrgChartRecord, field: OrgChartFieldKey | undefined): string | undefined {
  return valuesForField(record, field)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .map(scalarToText)
    .find(isAvatarUrl)
}

export function getOrgChartInitials(value: string): string {
  const words = value.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) {
    return '?'
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase()
  }

  return `${words[0][0]}${words[1][0]}`.toUpperCase()
}

export function resolveOrgChartDisplay(record: OrgChartRecord, mapping: OrgChartFieldMapping): OrgChartDisplay {
  const mappedTitle = resolveOrgChartField(record, mapping.title)
  const subtitle = resolveOrgChartField(record, mapping.subtitle)
  const title = mappedTitle || subtitle || record.id || 'Untitled position'
  const avatarText = resolveOrgChartField(record, mapping.avatar)
  const avatarUrl = avatarUrlForField(record, mapping.avatar)
  const badges = (mapping.badges ?? [])
    .map((field) => resolveOrgChartField(record, field, { useLastArrayEntry: true }))
    .filter(Boolean)

  return {
    title,
    ...(subtitle ? { subtitle } : {}),
    ...(avatarUrl ? { avatarUrl } : {}),
    badges,
    initials: getOrgChartInitials(avatarUrl ? title : avatarText || title),
  }
}

function normalizedParentId(parentId: unknown): string | null {
  if (typeof parentId !== 'string') {
    return null
  }

  const normalized = parentId.trim()
  return normalized || null
}

function createsParentCycle(nodeId: string, parentId: string, parentById: Map<string, string | null>): boolean {
  const visited = new Set<string>()
  let currentId: string | null = parentId

  while (currentId) {
    if (currentId === nodeId) {
      return true
    }
    if (visited.has(currentId)) {
      return true
    }

    visited.add(currentId)
    currentId = parentById.get(currentId) ?? null
  }

  return false
}

export function buildOrgChartHierarchy(records: OrgChartRecord[]): OrgChartHierarchy {
  const nodes: OrgChartHierarchyNode[] = records.map((record) => ({
    record,
    children: [],
  }))
  const nodesById = new Map(nodes.map((node) => [node.record.id, node]))
  const parentById = new Map<string, string | null>()

  for (const node of nodes) {
    const candidateParentId = normalizedParentId(node.record.parentId)
    const hasParent = candidateParentId !== null && nodesById.has(candidateParentId)
    const parentId =
      hasParent &&
      candidateParentId !== node.record.id &&
      !createsParentCycle(node.record.id, candidateParentId, parentById)
        ? candidateParentId
        : null
    parentById.set(node.record.id, parentId)
  }

  const roots: OrgChartHierarchyNode[] = []
  for (const node of nodes) {
    const parentId = parentById.get(node.record.id) ?? null
    const parent = parentId ? nodesById.get(parentId) : undefined
    if (parent) {
      parent.children.push(node)
    } else {
      roots.push(node)
    }
  }

  return { roots, nodesById, parentById }
}

export function getVisibleOrgChartNodeIds(
  hierarchy: OrgChartHierarchy,
  search: string,
  sidebarMapping: OrgChartFieldMapping
): Set<string> {
  const normalizedSearch = search.trim().toLocaleLowerCase()
  if (!normalizedSearch) {
    return new Set(hierarchy.nodesById.keys())
  }

  const visibleIds = new Set<string>()
  for (const node of Array.from(hierarchy.nodesById.values())) {
    const display = resolveOrgChartDisplay(node.record, sidebarMapping)
    const searchableText = `${display.title} ${display.subtitle ?? ''}`.toLocaleLowerCase()
    if (!searchableText.includes(normalizedSearch)) {
      continue
    }

    const visited = new Set<string>()
    let currentId: string | null = node.record.id
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId)
      visibleIds.add(currentId)
      currentId = hierarchy.parentById.get(currentId) ?? null
    }
  }

  return visibleIds
}

export function getOrgChartCanvasRows(
  hierarchy: OrgChartHierarchy,
  selectedId: string | null
): OrgChartHierarchyNode[][] {
  if (!selectedId) {
    return []
  }

  const selectedNode = hierarchy.nodesById.get(selectedId)
  if (!selectedNode) {
    return []
  }

  const rows: OrgChartHierarchyNode[][] = [[selectedNode]]
  const directReports = selectedNode.children
  if (directReports.length === 0) {
    return rows
  }

  rows.push(directReports)
  const secondLevelReports = directReports.flatMap((node) => node.children)
  if (secondLevelReports.length > 0) {
    rows.push(secondLevelReports)
  }

  return rows.slice(0, ORG_CHART_MAX_CANVAS_ROWS)
}

export function getOrgChartCanvasEdges(hierarchy: OrgChartHierarchy, selectedId: string | null): OrgChartCanvasEdge[] {
  const rows = getOrgChartCanvasRows(hierarchy, selectedId)
  const visibleIds = new Set(rows.flatMap((row) => row.map((node) => node.record.id)))
  return rows
    .flatMap((row) =>
      row.flatMap((node) =>
        node.children
          .filter((child) => visibleIds.has(child.record.id))
          .map((child) => ({
            sourceId: node.record.id,
            targetId: child.record.id,
          }))
      )
    )
    .filter((edge) => edge.sourceId !== edge.targetId)
}

function colorValue(value: unknown): string {
  if (Array.isArray(value)) {
    const values = value.map(scalarToText).filter(Boolean)
    return values.length > 0 ? values[values.length - 1] : ''
  }

  return scalarToText(value)
}

export function deriveOrgChartLegend(
  records: OrgChartRecord[],
  colorByKey: string | null | undefined
): OrgChartLegendEntry[] {
  if (!colorByKey) {
    return []
  }

  const values = Array.from(new Set(records.map((record) => colorValue(record[colorByKey])).filter(Boolean))).sort(
    (a, b) => a.localeCompare(b)
  )

  return values.map((value, index) => ({
    value,
    label: value,
    color: ORG_CHART_COLOR_PALETTE[index % ORG_CHART_COLOR_PALETTE.length],
  }))
}

export function getOrgChartColor(
  record: OrgChartRecord,
  colorByKey: string | null | undefined,
  legend: OrgChartLegendEntry[]
): string | undefined {
  if (!colorByKey) {
    return undefined
  }

  const value = colorValue(record[colorByKey])
  return legend.find((entry) => entry.value === value)?.color
}
